/**
 * Best fit — a greedy constructor over (time, route) candidates.
 *
 * objective 'revenue': grow by marginal revenue, with the min-£/day threshold
 *   as a crude cost proxy.
 * objective 'profit':  every add / right-size / prune decision is scored on
 *   revenue LESS running costs, so a departure that earns £600/day but wakes a
 *   whole extra steam engine (£2,000/day) is rejected.
 *
 * Phases:
 *   0  (Mode B only) seed shuttle candidates that CONNECT with the main line
 *   1  grow by marginal value, both directions competing on equal terms
 *   1.7 extend short workings to the terminus where it's free and it helps
 *   2  rescue — bring every carried trainload home, inventing trains if asked
 *   2.6 repair stock balance with positioning workings
 *   3  plan the fixed carriage sets
 *   4  prune departures that stopped paying
 *   5  re-check the no-unreturned contract on the FINISHED timetable — on
 *      EVERY day-type the timetable will run (the busiest day binds), never
 *      only the blended week it was designed against (Chris, 2026-09-04)
 *
 * Every candidate must satisfy headway, the single-line crossing rule, the
 * Network Rail path budget and the fleet caps — so Best fit never proposes a
 * timetable that could not actually be run.
 */
import type {
  CarriageSet, ClassDemand, CostModel, Departure, EngineParams, FitObjective,
  FleetCaps, Network, SetFormation, SetPlan, Slot, StrandedGroup,
} from './types'
import { HEADWAY_MIN, PASSENGER_MCT_MIN, gbp, runClasses } from './engine'

import { costFromDeps } from './costs'
import { fatalConflicts, pathOverage, stockImbalances } from './constraints'
import { applySetPlan, planSets } from './stock'
import { fmtMin } from './demand'
import {
  crossesTransfer, groupKeyFor, journeyMin, nearestStable, routeLabel, turnaroundAt,
} from './network'

/**
 * The rescue may fall back to this tighter headway. 45 minutes is a sensible
 * planning interval but it is a crude proxy: the SINGLE-LINE CROSSING check is
 * the physically meaningful constraint, and two trains leaving the same station
 * 20 minutes apart in the same direction cannot catch each other when they run
 * at the same speed. Holding 45 rigidly was blocking every rescue in a busy
 * afternoon and stranding passengers to protect a rule of thumb.
 */
export const HEADWAY_RELAXED_MIN = 20

/**
 * LOCOMOTIVES MAY NOT APPEAR OR VANISH (Chris, 2026-09-07). "Empty-stock moves
 * run diesel" gives an empty-stock move a diesel of its own ONLY when it runs
 * between the two sheds, Grosmont and Pickering. A move whose origin or
 * destination has no shed (Whitby, Goathland, Levisham) is worked by the engine
 * of the passenger train it feeds (the morning empty stock to Whitby is the
 * engine of the first train back) or that fed it (the evening empty stock home
 * is the engine that brought the last train in) — so it is left UNPINNED and
 * the diagram chainer (diagrams.ts::estimateDiagrams) joins it onto that
 * engine's day. Pinning it made a lone diesel drag the 09:00 empty stock to
 * Whitby and sit there all day while "Steam 2" appeared from nowhere for the
 * 10:00. Both places the pin is set (the stock-repair move and the phase-5
 * withdrawal) go through here.
 */
export const ecsPinFor = (net: Network, o: number, d: number, ecsDiesel: boolean | undefined): { tractionPin: 'diesel' } | Record<string, never> =>
  ecsDiesel && net.stables.includes(o) && net.stables.includes(d) ? { tractionPin: 'diesel' } : {}

export interface FitConfig {
  net: Network
  classes: ClassDemand[]
  params: EngineParams
  minGainPerDep: number
  maxPerDir: number
  objective: FitObjective
  rates: CostModel
  dieselLoco: number
  fleet: FleetCaps
  sets: CarriageSet[]
  /**
   * How many passengers we will accept being unable to get home. ZERO by
   * default and treated as a hard rule: the constructor keeps adding return
   * workings, inventing them and pairing them where a single working cannot
   * help, until it hits the allowance or can prove nothing more can reach them.
   */
  maxUnreturned: number
  /**
   * The day-types this timetable will actually run, each on its own sales
   * (weight 1 = one day). `classes` is the blended design day; the everyone-home
   * contract (rescue, prune and the final check) is judged on THESE — the
   * busiest day binds — so a Saturday crowd the week's average hides is still
   * brought home (Chris, 2026-09-04). Omitted = judged on `classes`, per day.
   */
  checkClasses?: ClassDemand[]
  firstMin: number
  lastMin: number
  /** Set formation for the modelled day (peak/off-peak measured capacities). */
  formation?: SetFormation
  /** Empty-stock positioning moves BETWEEN THE TWO SHEDS (Grosmont, Pickering)
   *  run on a diesel of their own — on by default in the UI (Chris, 2026-08-27).
   *  A move to or from a station with no shed is never pinned: it is worked by
   *  the engine of the train it feeds or that fed it (ecsPinFor; Chris,
   *  2026-09-07). */
  ecsDiesel?: boolean
}

const uniqSlots = (slots: Slot[]): Slot[] => {
  const seen = new Set<string>()
  return slots.filter((s) => {
    const k = `${s.time}|${s.route}`
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

/**
 * Mode B's chicken-and-egg problem, and the fix.
 *
 * A Pickering->Grosmont leg on its own captures almost nobody: most of that
 * demand is bound for Whitby, so without a shuttle to connect into there is no
 * itinerary and the passengers are simply lost. But `shuttleCandidates` derives
 * shuttles FROM existing main-line legs — so with an empty starting timetable
 * there are no shuttles to propose either. Greedy single-add therefore sees a
 * large cost and no revenue for every first move and builds NOTHING.
 *
 * This proposes the two halves TOGETHER, as one move: the main-line leg at the
 * time the demand actually wants, plus the earliest shuttle that connects after
 * the minimum connection time. Same trick as the pair rescue below — a greedy
 * search over single moves cannot cross a valley two moves wide.
 */
export function connectingPairCandidates(
  net: Network,
  classes: ClassDemand[],
  firstMin: number,
  lastMin: number
): { adds: Omit<Departure, 'coaches'>[]; label: string }[] {
  const t = net.transfers[0]
  if (!t) return []
  const x = t.station
  const out: { adds: Omit<Departure, 'coaches'>[]; label: string }[] = []
  const seen = new Set<string>()
  const dirOf = (o: number, d: number) => (d > o ? 'Northbound' : 'Southbound')

  const slots = [...classes.flatMap((c) => c.north), ...classes.flatMap((c) => c.south)]
  for (const s of slots) {
    if (crossesTransfer(net, s.o, s.d) === null) continue
    for (const off of [0, 15, 30, -15, -30]) {
      const t1 = Math.round((s.minutes + off) / 5) * 5
      if (t1 < firstMin || t1 > lastMin) continue
      const arr1 = t1 + journeyMin(net, s.o, x)
      const t2 = Math.ceil((arr1 + t.mctMin) / 5) * 5
      if (t2 > lastMin) continue
      const key = `${t1}|${s.o}|${x}|${t2}|${x}|${s.d}`
      if (seen.has(key)) continue
      seen.add(key)
      const leg1 = {
        id: `bp-${t1}-${s.o}-${x}`, direction: dirOf(s.o, x), time: fmtMin(t1), minutes: t1,
        enabled: true, o: s.o, d: x, group: groupKeyFor(net, s.o, x),
      }
      const leg2 = {
        id: `bp-${t2}-${x}-${s.d}`, direction: dirOf(x, s.d), time: fmtMin(t2), minutes: t2,
        enabled: true, o: x, d: s.d, group: groupKeyFor(net, x, s.d),
      }
      out.push({
        adds: [leg1, leg2],
        label: `${leg1.time} ${routeLabel(net, leg1.o, leg1.d)} + ${leg2.time} ${routeLabel(net, leg2.o, leg2.d)}`,
      })
    }
  }
  return out
}

/**
 * Mode B: propose shuttle departures that actually connect.
 *
 * A shuttle timetable pulled out of thin air is worthless - the whole point is
 * that a Whitby-bound passenger steps off the main line at Grosmont and onto a
 * shuttle after the minimum connection time. So candidates are generated FROM
 * the main-line legs: for each arrival at the transfer station, offer a shuttle
 * once the connection time has elapsed, and for each departure from it, offer a
 * shuttle that gets back in time to feed it.
 */
export function shuttleCandidates(
  net: Network,
  deps: Departure[],
  firstMin: number,
  lastMin: number
): { s: Slot; dir: string }[] {
  const t = net.transfers[0]
  if (!t) return []
  const x = t.station
  const far = net.stations.length - 1
  const out: { s: Slot; dir: string }[] = []
  const seen = new Set<string>()

  const add = (mins: number, o: number, d: number) => {
    const m = Math.ceil(mins / 5) * 5
    if (m < firstMin || m > lastMin) return
    const key = `${m}|${o}|${d}`
    if (seen.has(key)) return
    seen.add(key)
    out.push({
      s: {
        minutes: m,
        time: fmtMin(m),
        paxPerDay: 0,
        netPerPax: 0,
        o,
        d,
        route: routeLabel(net, o, d),
      },
      dir: d > o ? 'Northbound' : 'Southbound',
    })
  }

  const legs = deps.filter((d) => d.enabled && !d.positioning)
  for (const l of legs) {
    // A main-line arrival at the transfer node feeds an onward shuttle.
    if (l.d === x) {
      const arr = l.minutes + journeyMin(net, l.o, l.d)
      for (const off of [0, 5, 10]) add(arr + t.mctMin + off, x, far)
    }
    // A main-line departure FROM the transfer node needs a shuttle back that
    // arrives in time for people to make it.
    if (l.o === x) {
      const need = l.minutes - t.mctMin - journeyMin(net, x, far)
      for (const off of [0, 5, 10]) add(need - off, far, x)
    }
  }
  return out
}

/**
 * EVERYONE HOME, judged the way the verdict judges it: on every day-type the
 * timetable runs, each as ONE day, the busiest day binding. `worst` is what the
 * allowance (passengers a day) is compared with; `total` is the sum over the
 * day-types, so a rescue that only helps Saturday still scores; `stranded` is
 * every stranded group across them, for the rescue's candidate search.
 */
export function homeCheck(cfg: FitConfig, deps: Departure[]): { worst: number; total: number; stranded: StrandedGroup[] } {
  const classes = cfg.checkClasses && cfg.checkClasses.length > 0 ? cfg.checkClasses : cfg.classes
  let worst = 0, total = 0
  const stranded = new Map<string, StrandedGroup>()
  for (const c of classes) {
    const r = runClasses(cfg.net, [{ ...c, weight: 1 }], deps, cfg.params)
    worst = Math.max(worst, r.unreturned)
    total += r.unreturned
    for (const g of r.stranded) {
      const k = `${g.time}|${g.route}|${g.reason}`
      const cur = stranded.get(k)
      if (cur) cur.count += g.count
      else stranded.set(k, { ...g })
    }
  }
  return { worst, total, stranded: [...stranded.values()] }
}

/**
 * Rescue loop — add return workings until every carried trainload gets home (or
 * nothing more can help).
 *
 * TWO BUGS THIS FIXES, both found by Chris on the first real Mode B run
 * ("310 passengers still can't be brought home").
 *
 * 1. A CONNECTING journey cannot be rescued one working at a time. A Whitby
 *    crowd going home to Pickering in Mode B needs a shuttle Whitby->Grosmont
 *    AND a main-line Grosmont->Pickering. Adding either alone rescues NOBODY,
 *    so a greedy single-add search scored every candidate at zero and gave up
 *    while the fix was sitting one move deeper. The loop now also considers
 *    PAIRS of workings for any stranded journey that crosses a transfer node.
 *
 * 2. The path-budget test was absolute. Once a timetable was over budget,
 *    `pathsExceeded` rejected every further candidate — including workings that
 *    never touch Network Rail. The test is now marginal: does this add make the
 *    path problem worse? And if the ONLY way to get people home is more paths
 *    than we run today, the loop says so explicitly and quantifies it, rather
 *    than failing silently. That number is a finding worth having.
 */
export function rescueTrains(
  cfg: FitConfig,
  startDeps: Departure[],
  inventTrains: boolean,
  strandedLimit: number
): { deps: Departure[]; log: string[] } {
  const { net, classes, sets, firstMin, lastMin } = cfg
  const log: string[] = []
  let deps = [...startDeps]

  /** Biggest rake a service group can actually be given from the roster. */
  const maxCoachFor = (group: string): number => {
    const eligible = sets.filter((s) => s.active && (!s.group || s.group === group))
    return eligible.length > 0 ? Math.max(...eligible.map((s) => s.coaches)) : 2
  }

  const mk = (dir: string, s: Slot, tag = 'rx'): Departure => {
    const group = groupKeyFor(net, s.o, s.d)
    return {
      id: `${tag}-${dir}-${s.time}-${s.route}`,
      direction: dir,
      time: s.time,
      minutes: s.minutes,
      enabled: true,
      coaches: maxCoachFor(group),
      o: s.o,
      d: s.d,
      group,
    }
  }
  const dirOf = (o: number, d: number) => (d > o ? 'Northbound' : 'Southbound')
  const headwayOk = (dir: string, s: Slot, d: Departure[], minGap: number) =>
    d.filter((x) => x.direction === dir && x.o === s.o)
      .every((x) => Math.abs(x.minutes - s.minutes) >= minGap)
  const inWindow = (m: number) => m >= firstMin && m <= lastMin
  const dup = (dir: string, s: Slot, d: Departure[]) =>
    d.some((x) => x.direction === dir && x.time === s.time && x.o === s.o && x.d === s.d)

  const mkSlot = (mins: number, o: number, d: number): Slot => ({
    minutes: mins, time: fmtMin(mins), paxPerDay: 0, netPerPax: 0,
    o, d, route: routeLabel(net, o, d),
  })

  /** Why an addition was rejected — so the give-up message can name the cause. */
  interface Reject { headway: number; crossing: number; paths: number; window: number }
  let rejects: Reject = { headway: 0, crossing: 0, paths: 0, window: 0 }
  const resetRejects = () => { rejects = { headway: 0, crossing: 0, paths: 0, window: 0 } }

  /**
   * Can this set of additions go in? `allowPaths` relaxes only the path budget,
   * which is an external commercial constraint rather than a physical one.
   */
  const admissible = (
    adds: Departure[], d: Departure[], baseConf: number, baseOver: number,
    allowPaths: boolean, minGap: number
  ): boolean => {
    const next = [...d, ...adds]
    for (const a of adds) {
      if (!inWindow(a.minutes)) { rejects.window += 1; return false }
      if (d.some((x) => x.direction === a.direction && x.time === a.time && x.o === a.o && x.d === a.d)) return false
      if (!headwayOk(a.direction, mkSlot(a.minutes, a.o, a.d), [...d, ...adds.filter((z) => z !== a)], minGap)) {
        rejects.headway += 1
        return false
      }
    }
    // The crossing rule is never relaxed - it is physics, not a preference.
    if (fatalConflicts(net, next) > baseConf) { rejects.crossing += 1; return false }
    if (!allowPaths && pathOverage(net, next) > baseOver) { rejects.paths += 1; return false }
    return true
  }

  const observed: { s: Slot; dir: string }[] = [
    ...uniqSlots(classes.flatMap((c) => c.north)).filter((s) => inWindow(s.minutes)).map((s) => ({ s, dir: 'Northbound' })),
    ...uniqSlots(classes.flatMap((c) => c.south)).filter((s) => inWindow(s.minutes)).map((s) => ({ s, dir: 'Southbound' })),
  ]

  const OFFSETS = [0, 15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180]

  for (;;) {
    const cur = homeCheck(cfg, deps)
    if (cur.worst <= strandedLimit) break
    const baseConf = fatalConflicts(net, deps)
    const baseOver = pathOverage(net, deps)

    // --- single-working candidates -------------------------------------
    // Search from the group's PREFERRED return time (full stay) AND from the
     // earliest legally possible one (arrival + turnaround). The classifier calls
     // a group fixable on the second of those, so the rescue has to look there
     // too — otherwise a late-afternoon Whitby crowd is declared rescuable and
     // then never rescued, because every candidate at the full-stay time falls
     // outside the operating window.
    const searchFrom = (g: { earliestMin: number; arrivalMin: number; o: number }): number[] => {
      // Passenger feasibility, not loco turnaround — the classifier calls a
      // group fixable from arrival + the passenger MCT, so search from there.
      const soonest = Math.ceil((g.arrivalMin + PASSENGER_MCT_MIN) / 5) * 5
      const preferred = Math.ceil(g.earliestMin / 5) * 5
      return preferred === soonest ? [preferred] : [preferred, soonest]
    }
    const synth: { s: Slot; dir: string }[] = inventTrains
      ? cur.stranded.flatMap((g) =>
          searchFrom(g).flatMap((base) =>
            OFFSETS.map((off) => {
              const mns = base + off
              if (!inWindow(mns)) return null
              return { s: mkSlot(mns, g.o, g.d), dir: dirOf(g.o, g.d) }
            }).filter((x): x is { s: Slot; dir: string } => x !== null)
          )
        )
      : []
    const shuttles = net.transfers.length > 0 ? shuttleCandidates(net, deps, firstMin, lastMin) : []
    const singles = [...observed, ...synth, ...shuttles]

    // --- connecting PAIRS ----------------------------------------------
    // The fix for bug 1. For a stranded journey that must cross a transfer
    // node, propose leg1 (origin -> transfer) together with the earliest legal
    // leg2 (transfer -> destination), and score them as one move.
    const pairs: { adds: Departure[]; label: string }[] = []
    if (inventTrains) {
      for (const g of cur.stranded) {
        const t = crossesTransfer(net, g.o, g.d)
        if (!t) continue
        const x = t.station
        for (const t1 of searchFrom(g).flatMap((base) => OFFSETS.map((off) => base + off))) {
          if (!inWindow(t1)) continue
          const arr1 = t1 + journeyMin(net, g.o, x)
          for (const off2 of [0, 5, 10, 15, 30]) {
            const t2 = Math.ceil((arr1 + t.mctMin) / 5) * 5 + off2
            if (!inWindow(t2)) continue
            const leg1 = mk(dirOf(g.o, x), mkSlot(t1, g.o, x), 'rxa')
            const leg2 = mk(dirOf(x, g.d), mkSlot(t2, x, g.d), 'rxb')
            pairs.push({
              adds: [leg1, leg2],
              label: `${leg1.time} ${routeLabel(net, leg1.o, leg1.d)} + ${leg2.time} ${routeLabel(net, leg2.o, leg2.d)}`,
            })
          }
        }
      }
    }

    // Escalate one concession at a time, cheapest first: full headway ->
    // tighter headway. The path budget is NEVER a tier: it is today's real NR
    // usage and Chris's standing rule is that the model never solves anything
    // by planning for more paths — a rescue that breached it produced a
    // disqualified timetable and a rake stranded at Whitby. When no path-legal
    // rescue exists, the caller withdraws the outbound that strands them.
    const tiers: { allowPaths: boolean; minGap: number }[] = [
      { allowPaths: false, minGap: HEADWAY_MIN },
      { allowPaths: false, minGap: HEADWAY_RELAXED_MIN },
    ]
    let chosen: { adds: Departure[]; label: string; rescued: number; overPaths: boolean; tightGap: boolean } | null = null
    for (const tier of tiers) {
      // Count rejections for THIS tier only — the last tier attempted is the
      // one whose blocker is worth reporting.
      resetRejects()
      let best: { adds: Departure[]; label: string; rescued: number } | null = null
      for (const { s, dir } of singles) {
        if (dup(dir, s, deps)) continue
        const add = mk(dir, s)
        if (!admissible([add], deps, baseConf, baseOver, tier.allowPaths, tier.minGap)) continue
        const rescued = cur.total - homeCheck(cfg, [...deps, add]).total
        if (!best || rescued > best.rescued) {
          best = { adds: [add], label: `${s.time} ${routeLabel(net, s.o, s.d)}`, rescued }
        }
      }
      if (!best || best.rescued < 1) {
        for (const p of pairs) {
          if (!admissible(p.adds, deps, baseConf, baseOver, tier.allowPaths, tier.minGap)) continue
          const rescued = cur.total - homeCheck(cfg, [...deps, ...p.adds]).total
          if (!best || rescued > best.rescued) best = { ...p, rescued }
        }
      }
      if (best && best.rescued >= 1) {
        chosen = { ...best, overPaths: tier.allowPaths, tightGap: tier.minGap < HEADWAY_MIN }
        break
      }
    }

    if (!chosen) {
      // Name the binding constraint rather than shrugging. Which check rejected
      // the most candidates is the most useful thing we can tell a planner.
      const worst = (Object.entries(rejects) as [keyof Reject, number][])
        .sort((a, b) => b[1] - a[1])[0]
      const cause = !worst || worst[1] === 0
        ? 'no candidate working reached them at all'
        : worst[0] === 'paths'
          ? 'the Network Rail path budget is binding'
          : worst[0] === 'crossing'
            ? 'every option creates a single-line crossing conflict a loop cannot absorb'
            : worst[0] === 'headway'
              ? `every option breaks the ${HEADWAY_MIN}-minute headway from an existing working`
              : `every option falls outside the ${fmtMin(firstMin)}–${fmtMin(lastMin)} operating window`
      const hint = worst && worst[0] === 'window'
        ? `Widen the operating window past ${fmtMin(lastMin)} — with the connections these journeys need, the day is not long enough.`
        : worst && worst[0] === 'paths'
          ? 'The path budget is what we run today and the model never plans for more — the outbound working that strands them will be withdrawn instead.'
          : worst && worst[0] === 'crossing'
            ? 'Retime an existing working to free a crossing, or short-work one of them.'
            : 'Widen the operating window, or accept that these are one-way travellers.'
      // Without the contract the rescue may only use workings that already exist
      // or connect to one — it does not invent trains. Say so, because ticking the
      // contract is usually the whole answer.
      const action = inventTrains
        ? hint
        : `Tick "No unreturned passengers" and ★ Best fit will add the workings it takes. ${hint}`
      log.push(`⚠ ${Math.round(cur.worst)} passengers a day (on the busiest day-type) still can't be brought home — ${cause}. ${action}`)
      break
    }

    deps = [...deps, ...chosen.adds].sort((a, b) => a.minutes - b.minutes)
    const pathNote = chosen.overPaths
      ? ` ⚠ this needs ${pathOverage(net, deps) - baseOver} more Network Rail path${pathOverage(net, deps) - baseOver === 1 ? '' : 's'} than we run today`
      : ''
    log.push(
      chosen.adds.length > 1
        ? `Added a connecting pair — ${chosen.label} — brings ${Math.round(chosen.rescued)} passengers home (neither working achieves anything on its own)${pathNote}`
        : `Added ${chosen.label} — brings ${Math.round(chosen.rescued)} passengers home${pathNote}`
    )
  }
  return { deps, log }
}

export function bestFit(cfg: FitConfig): { deps: Departure[]; log: string[]; plan: SetPlan } {
  const {
    net, classes, params, minGainPerDep, maxPerDir, objective, rates, dieselLoco,
    fleet, sets, maxUnreturned, firstMin, lastMin,
  } = cfg
  const log: string[] = []
  let deps: Departure[] = []
  const w = classes.reduce((t, c) => t + c.weight, 0) || 1
  const maxCoach = Math.max(2, ...sets.filter((s) => s.active).map((s) => s.coaches))
  /** Biggest rake a service group can be given - the shuttle unit is smaller. */
  const maxCoachFor = (group: string): number => {
    const eligible = sets.filter((s) => s.active && (!s.group || s.group === group))
    return eligible.length > 0 ? Math.max(...eligible.map((s) => s.coaches)) : 2
  }
  const run = (d: Departure[]) => runClasses(net, classes, d, params)

  // MARGINAL economics: a working's add/keep decision is scored on the costs
  // that decision actually moves — loco running, crew turns, carriages,
  // overhaul provision, NR access. Own-metals P-way/S&T, boxes and fixed
  // overhead are the cost of opening the railway for the day: charging them to
  // the FIRST candidate working (as `.total` did) made every first move look
  // like a £10k/week loss, so the greedy could never start and "no service
  // pays its way". The finished timetable's P&L still carries the full total.
  //
  // ★ The P&L cost model does NOT steer this decision (Chris, 2026-09-02:
  // "the build logic should not have changed just because we changed the
  // source of the costs"). Scoring add/keep/prune on the accounts AVERAGE
  // per turn (PR #252) loaded essentially-fixed lines (rolling-stock hire)
  // onto every candidate working, so almost nothing "paid its way" and the
  // season collapsed to 1–2 turns a day. The P&L prices the FINISHED design
  // (costLines.ts::pnlDayCost); the greedy decides on the true incremental.
  const value = (d: Departure[]): number => {
    const r = run(d).revenue
    return objective === 'profit'
      ? r - costFromDeps(net, d, rates, dieselLoco, fleet).marginal * w
      : r
  }
  const planFor = (d: Departure[]) =>
    planSets(net, classes, d, sets, params, objective, rates, dieselLoco, fleet, maxUnreturned, cfg.formation ?? 'peak')
  const plannedValue = (d: Departure[]): number => {
    const p = planFor(d)
    const applied = applySetPlan(d, p)
    const r = run(applied)
    const base = objective === 'profit'
      ? r.revenue - costFromDeps(net, applied, rates, dieselLoco, fleet, p.carriages, p.setsInTraffic).marginal * w
      : r.revenue
    return base - Math.max(0, r.unreturned - maxUnreturned) * 1e6
  }

  const period = w > 1 ? 'week' : 'day'
  const unit = objective === 'profit'
    ? `/${period} marginal contribution (engine, carriage, crew, fuel & NR access — the day-fixed infra/overhead block sits on the day, not on one working)`
    : `/${period}`

  const mk = (dir: string, s: Slot, coaches: number): Departure => ({
    id: `bf-${dir}-${s.time}-${s.route}`,
    direction: dir,
    time: s.time,
    minutes: s.minutes,
    enabled: true,
    coaches,
    o: s.o,
    d: s.d,
    group: groupKeyFor(net, s.o, s.d),
  })
  const headwayOk = (dir: string, s: Slot, d: Departure[]) =>
    d.filter((x) => x.direction === dir && x.o === s.o)
      .every((x) => Math.abs(x.minutes - s.minutes) >= HEADWAY_MIN)
  const inWindow = (s: Slot) => s.minutes >= firstMin && s.minutes <= lastMin
  const dupOrBlocked = (dir: string, s: Slot, d: Departure[]) =>
    d.some((x) => x.direction === dir && x.time === s.time && x.o === s.o && x.d === s.d) ||
    !headwayOk(dir, s, d)
  const addsCrossingConflict = (dir: string, s: Slot, d: Departure[], baseConf: number) =>
    fatalConflicts(net, [...d, mk(dir, s, maxCoach)]) > baseConf
  // Marginal, not absolute: an absolute test blocks EVERY further add once the
  // timetable is over budget, including workings that never touch Network Rail.
  const breaksPaths = (dir: string, s: Slot, d: Departure[]) =>
    pathOverage(net, [...d, mk(dir, s, maxCoach)]) > pathOverage(net, d)

  const north = classes.flatMap((c) => c.north)
  const south = classes.flatMap((c) => c.south)
  const nCands = uniqSlots(north).filter(inWindow)
  const sCands = uniqSlots(south).filter(inWindow)

  // In Mode B, a Pickering->Whitby demand block cannot be served by an observed
  // through departure any more, so restrict the observed candidates to spans the
  // network's groups can actually work, and let the shuttle generator supply
  // the branch. Without this the constructor keeps proposing through workings
  // and never discovers the connecting pattern.
  const workable = (s: Slot) => {
    if (net.transfers.length === 0) return true
    return crossesTransfer(net, s.o, s.d) === null
  }
  const allCands: { s: Slot; dir: string }[] = [
    ...nCands.filter(workable).map((s) => ({ s, dir: 'Northbound' })),
    ...sCands.filter(workable).map((s) => ({ s, dir: 'Southbound' })),
  ]
  // Mode B: a through demand block still has to reach Grosmont, so offer the
  // main-line leg at the observed desired time.
  if (net.transfers.length > 0) {
    const x = net.transfers[0].station
    for (const [dir, arr] of [['Northbound', nCands], ['Southbound', sCands]] as const) {
      for (const s of arr) {
        if (crossesTransfer(net, s.o, s.d) === null) continue
        const o = dir === 'Northbound' ? s.o : x
        const d = dir === 'Northbound' ? x : s.d
        if (o === d) continue
        allCands.push({ s: { ...s, o, d, route: routeLabel(net, o, d) }, dir })
      }
    }
  }

  // ---- Phase 1: grow by marginal value, BOTH DIRECTIONS AT ONCE. Whitby-side
  // southbound demand competes on equal terms with Pickering-side northbound,
  // so balanced out-and-back patterns emerge from the demand itself.
  // Mode B needs its connecting pairs in the GROWTH pool, not only the rescue.
  // A main-line leg on its own carries nobody bound for Whitby, so it never
  // clears the threshold, and shuttle candidates are derived from legs that do
  // not exist yet - so the constructor built nothing at all. Proposing both
  // halves as one move is what breaks the deadlock.
  const pairPool = net.transfers.length > 0
    ? connectingPairCandidates(net, classes, firstMin, lastMin)
    : []
  const slotOf = (a: Departure): Slot =>
    ({ minutes: a.minutes, time: a.time, paxPerDay: 0, netPerPax: 0, o: a.o, d: a.d, route: '' })

  for (;;) {
    const cur = value(deps)
    const baseConf = fatalConflicts(net, deps)
    const pool = net.transfers.length > 0
      ? [...allCands, ...shuttleCandidates(net, deps, firstMin, lastMin)]
      : allCands
    let best: { adds: Departure[]; label: string; gain: number } | null = null
    for (const { s, dir } of pool) {
      if (deps.filter((d) => d.direction === dir).length >= maxPerDir) continue
      if (dupOrBlocked(dir, s, deps)) continue
      if (addsCrossingConflict(dir, s, deps, baseConf)) continue
      if (breaksPaths(dir, s, deps)) continue
      const add = mk(dir, s, maxCoachFor(groupKeyFor(net, s.o, s.d)))
      const gain = value([...deps, add]) - cur
      if (!best || gain > best.gain) {
        best = { adds: [add], label: `${s.time} ${routeLabel(net, s.o, s.d)}`, gain }
      }
    }
    for (const cand of pairPool) {
      const adds = cand.adds.map((a) => ({ ...a, coaches: maxCoachFor(a.group ?? 'main') }) as Departure)
      if (adds.some((a) => deps.some((x) => x.direction === a.direction && x.time === a.time && x.o === a.o && x.d === a.d))) continue
      if (adds.some((a) => !headwayOk(a.direction, slotOf(a), [...deps, ...adds.filter((z) => z !== a)]))) continue
      const next = [...deps, ...adds]
      if (fatalConflicts(net, next) > baseConf) continue
      if (pathOverage(net, next) > pathOverage(net, deps)) continue
      const gain = value(next) - cur
      if (!best || gain > best.gain) best = { adds, label: cand.label, gain }
    }
    if (!best || best.gain < minGainPerDep) {
      // D3 guard: an empty result must NEVER be silent. Say exactly why nothing
      // cleared the bar — in the live test the (then-broken) cost model made
      // every service "lose money", Best fit built an empty timetable without a
      // word, and the empty timetable went on to win Compare.
      if (deps.length === 0) {
        log.push(best
          ? `⚠ BEST FIT ADDED NOTHING. The best candidate (${best.label}) was worth ${gbp(best.gain)}${unit} of PROFIT (its fares less the extra cost of running it), under the ${gbp(minGainPerDep)} minimum profit per working. Under this cost model no service pays its way — check the cost model and the minimum before concluding the railway should run no trains. An empty timetable is not an answer.`
          : '⚠ BEST FIT ADDED NOTHING: no feasible candidate exists inside the operating window, headway, crossing and path constraints. An empty timetable is not an answer.')
      }
      break
    }
    deps = [...deps, ...best.adds]
    log.push(
      best.adds.length > 1
        ? `Added a connecting pair - ${best.label} - worth ${gbp(best.gain)}${unit} (neither leg is worth running without the other)`
        : `Added ${best.label} - worth ${gbp(best.gain)}${unit}`
    )
  }

  // ---- Phase 1.7: EXTEND short workings to the terminus when it helps. On a
  // day-rate cost model an extension is free (same engine, same rake, same
  // clock), so if running through brings more people home or earns more — with
  // no fatal crossing, no worse stock balance and no path breach — do that
  // rather than invent a whole extra train.
  for (const d of [...deps]) {
    if (!d.enabled) continue
    const group = net.groups.find((g) => g.key === (d.group ?? groupKeyFor(net, d.o, d.d)))
    const terminus = d.d > d.o ? (group?.to ?? net.stations.length - 1) : (group?.from ?? 0)
    if (d.d === terminus) continue
    const next = deps.map((x) => (x.id === d.id ? { ...x, d: terminus } : x))
    if (fatalConflicts(net, next) > fatalConflicts(net, deps)) continue
    if (stockImbalances(net, next).length > stockImbalances(net, deps).length) continue
    if (pathOverage(net, next) > pathOverage(net, deps)) continue
    const curR = run(deps); const nextR = run(next)
    const curV = value(deps); const nextV = value(next)
    if (nextR.unreturned < curR.unreturned - 0.5 || nextV > curV + 1) {
      const gainTxt = nextR.unreturned < curR.unreturned - 0.5
        ? `brings ${Math.round(curR.unreturned - nextR.unreturned)} more home`
        : `worth ${gbp(nextV - curV)} more`
      log.push(`Extended ${d.time} ${routeLabel(net, d.o, d.d)} through to ${net.stations[terminus].name} — ${gainTxt}`)
      deps = next
    }
  }

  // ---- Phase 2: rescue. The allowance is a hard rule, so the rescue always
  // gets to invent workings — it is the only way to honour a zero allowance.
  const strandedLimit = Math.max(0.5, maxUnreturned)
  {
    const r = rescueTrains(cfg, deps, true, strandedLimit)
    deps = r.deps
    log.push(...r.log)
  }

  // ---- Phase 2.6: STOCK BALANCE repair at the no-stabling stations. A
  // departure can't leave before its rake has arrived and nothing may be left
  // overnight. Repair with positioning workings — charged like any other
  // working, so the objective sees the TRUE cost of serving e.g. the 10:00
  // ex-Whitby. A function because the phase-5 rescue can ADD trains after this
  // ran (a late extra into Whitby re-strands a rake) — every rescue round
  // repairs again, so the finished timetable is never left unstabled.
  const repairStock = () => {
  for (let guard = 0; guard < 12; guard++) {
    const issues = stockImbalances(net, deps)
    if (issues.length === 0) break
    const baseConf = fatalConflicts(net, deps)
    const synth: { s: Slot; dir: string }[] = []
    for (const iss of issues) {
      const stable = nearestStable(net, iss.station)
      if (iss.kind === 'need') {
        const latest = Math.floor(
          (iss.atMin - turnaroundAt(net, iss.station) - journeyMin(net, stable, iss.station)) / 5
        ) * 5
        for (const off of [0, 15, 30, 45, 60]) {
          const dep = latest - off
          // Positioning moves may run up to an hour outside the passenger window.
          if (dep >= firstMin - 60) {
            synth.push({
              s: { minutes: dep, time: fmtMin(dep), paxPerDay: 0, netPerPax: 0, o: stable, d: iss.station, route: routeLabel(net, stable, iss.station) },
              dir: iss.station > stable ? 'Northbound' : 'Southbound',
            })
          }
        }
      } else {
        const earliest = Math.ceil(iss.atMin / 5) * 5
        for (const off of [0, 15, 30, 45, 60]) {
          const dep = earliest + off
          if (dep <= lastMin + 60) {
            synth.push({
              s: { minutes: dep, time: fmtMin(dep), paxPerDay: 0, netPerPax: 0, o: iss.station, d: stable, route: routeLabel(net, iss.station, stable) },
              dir: stable > iss.station ? 'Northbound' : 'Southbound',
            })
          }
        }
      }
    }
    // Score by RAKES rescued, not issues closed: "2 rakes left overnight" is
    // one issue, so an ECS that saves one of the two used to score zero and be
    // rejected — Best fit then finished with stranded stock and shrugged.
    const weightOf = (d: Departure[]) => stockImbalances(net, d).reduce((t, i) => t + i.count, 0)
    const baseWeight = weightOf(deps)
    let best: { s: Slot; dir: string; fixed: number; gain: number; pos: boolean } | null = null
    for (const { s, dir, pos } of [
      ...allCands.map((c) => ({ ...c, pos: false })),
      ...synth.map((c) => ({ ...c, pos: true })),
    ]) {
      if (dupOrBlocked(dir, s, deps)) continue
      if (addsCrossingConflict(dir, s, deps, baseConf)) continue
      if (breaksPaths(dir, s, deps)) continue
      const next = [...deps, mk(dir, s, maxCoach)]
      const fixed = baseWeight - weightOf(next)
      if (fixed <= 0) continue
      const gain = value(next) - value(deps)
      if (!best || fixed > best.fixed || (fixed === best.fixed && gain > best.gain)) {
        best = { s, dir, fixed, gain, pos }
      }
    }
    if (!best) {
      // No legal balancing working exists — every candidate falls outside the
      // operating window, breaks the path budget or adds a crossing conflict
      // (e.g. a late arrival into Whitby whose only possible return would
      // leave after the day has ended). A timetable that cannot stable its
      // stock is non-workable, so the working that CAUSES the strand comes
      // out and the balance is re-checked — the day simply cannot afford
      // that working. Never ship an unstabled timetable and shrug (law 8).
      const guilty = (() => {
        for (const iss of issues) {
          if (iss.kind === 'need') {
            // The departure that leaves before any rake has arrived — the
            // issue's label names it exactly; earliest-from-station backstop.
            const fromHere = deps.filter((x) => x.enabled && !x.positioning && x.o === iss.station)
            const g = fromHere.find((x) => x.time === iss.label)
              ?? fromHere.sort((a, b) => a.minutes - b.minutes)[0]
            if (g) return g
          } else {
            // The last arrival into the no-stable station stranded its rake.
            const g = deps
              .filter((x) => x.enabled && x.d === iss.station)
              .sort((a, b) => (b.arrMin ?? b.minutes) - (a.arrMin ?? a.minutes))[0]
            if (g) return g
          }
        }
        return null
      })()
      if (!guilty) {
        log.push(`⚠ Stock balance could not be fully repaired: ${issues.map((i) => i.msg).join('; ')}`)
        break
      }
      deps = deps.filter((x) => x.id !== guilty.id)
      log.push(
        `Removed the ${guilty.time} ${routeLabel(net, guilty.o, guilty.d)} — no balancing working fits inside the operating window and path budget, and a day that cannot stable its stock is non-workable`
      )
      continue
    }
    // A synth move is empty-coaching-stock: it carries nobody and says so. The
    // ECS-diesel pin applies only between the two sheds (ecsPinFor): a move to
    // or from Whitby, Goathland or Levisham stays unpinned, so the diagram
    // chainer gives it to the engine of the train it feeds or that fed it.
    const move = { ...mk(best.dir, best.s, maxCoach), ...(best.pos ? { positioning: true, ...ecsPinFor(net, best.s.o, best.s.d, cfg.ecsDiesel) } : {}) }
    deps = [...deps, move].sort((a, b) => a.minutes - b.minutes)
    log.push(`Added ${best.s.time} ${routeLabel(net, best.s.o, best.s.d)} — ${best.pos ? 'positioning working' : 'revenue working'} to balance stock`)
  }
  }
  repairStock()

  // ---- Phase 3: CARRIAGE-SET PLAN. A rake is fixed all day.
  let plan = planFor(deps)
  deps = applySetPlan(deps, plan)
  for (const ch of plan.chains) {
    if (!ch.set) continue
    const legsTxt = ch.legs.map((l) => `${l.time} ${net.stations[l.o].name}→${net.stations[l.d].name}`).join(' · ')
    log.push(`Set ${ch.set.code} (${ch.set.coaches === 0 ? 'Autocar' : `${ch.set.coaches} coaches`}) works ${legsTxt}`)
  }
  for (const warn of plan.warnings) log.push(`⚠ ${warn}`)

  // ---- Phase 4: prune non-payers that strand nobody, under real capacities.
  for (const d of [...deps].filter((x) => x.enabled)) {
    const keepVal = plannedValue(deps)
    const without = deps.filter((x) => x.id !== d.id)
    const r = homeCheck(cfg, applySetPlan(without, planFor(without)))
    const imbWeight = (dd: Departure[]) => stockImbalances(net, dd).reduce((t, i) => t + i.count, 0)
    if (keepVal - plannedValue(without) < minGainPerDep &&
        r.worst <= strandedLimit &&
        imbWeight(without) <= imbWeight(deps)) {
      deps = without
      log.push(objective === 'profit'
        ? `Removed ${d.time} ${routeLabel(net, d.o, d.d)} — its fares did not beat the extra cost of running it by the ${gbp(minGainPerDep)} minimum`
        : `Removed ${d.time} ${routeLabel(net, d.o, d.d)} — earned under ${gbp(minGainPerDep)}/${period}`)
    }
  }
  plan = planFor(deps)
  deps = applySetPlan(deps, plan)

  // ---- Phase 5: THE CONTRACT. "No unreturned passengers" is only meaningful
  // on the FINISHED timetable with the REAL assigned rakes: the set plan can
  // undo the phase-2 rescue (a train tested at the biggest rake may receive a
  // 192-seat set, or no set at all). So re-check, and loop rescue -> set plan
  // until the promise holds or nothing more can be added — in which case say
  // EXACTLY why, loudly.
  {
    for (let round = 0; round < 5; round++) {
      const r = homeCheck(cfg, deps)
      if (r.worst <= strandedLimit) break
      const before = deps.length
      const res = rescueTrains(cfg, deps, true, strandedLimit)
      log.push(...res.log)
      if (res.deps.length === before) {
        // No path-legal rescue exists. The honest move is to stop SELLING the
        // outbound that strands its riders: it keeps running EMPTY (the rake
        // move keeps the stock balance; removing it outright made the repair
        // re-add the same slot as a revenue working — an endless tug-of-war)
        // and its demand spills to earlier departures, which return fine.
        // Never answer by planning for more Network Rail paths.
        const guilty = [...r.stranded].sort((a, b) => b.count - a.count)[0]
        const out = guilty
          ? deps.find((x) => x.enabled && !x.positioning && !x.returnOnly && x.time === guilty.time && x.o === guilty.d && x.d === guilty.o)
          : undefined
        if (!out) break
        // Does anyone ride this working HOME? Then it stays a passenger train,
        // sold to returners only — making it empty stock stranded the very
        // people it was bringing back, and the two trains of a Sunday then
        // withdrew each other until nothing ran (Pink, 2026-09-04).
        const carriesHome = (cfg.checkClasses && cfg.checkClasses.length > 0 ? cfg.checkClasses : cfg.classes)
          .some((c) => (runClasses(net, [{ ...c, weight: 1 }], deps, cfg.params).perDep.find((p) => p.id === out.id)?.retAlloc ?? 0) > 0.5)
        if (carriesHome) {
          deps = deps.map((x) => (x.id === out.id ? { ...x, returnOnly: true } : x))
          log.push(`${out.time} ${routeLabel(net, out.o, out.d)} now carries RETURN passengers only — its ${Math.round(guilty.count)} outward riders could not be brought home inside today's Network Rail paths, so it is not sold outward; they take an earlier train or the sale is lost.`)
        } else {
          deps = deps.map((x) => (x.id === out.id ? { ...x, positioning: true, ...ecsPinFor(net, out.o, out.d, cfg.ecsDiesel) } : x))
          log.push(`${out.time} ${routeLabel(net, out.o, out.d)} now runs EMPTY (stock move only) — its ${Math.round(guilty.count)} riders could not be brought home inside today's Network Rail paths, so it is not sellable; that demand spills to earlier departures instead.`)
        }
      } else {
        deps = res.deps
      }
      // A rescue train into a no-stable station re-strands a rake — repair
      // again before re-planning, or the finished timetable ships unstabled.
      repairStock()
      plan = planFor(deps)
      deps = applySetPlan(deps, plan)
    }
    const finalR = homeCheck(cfg, deps)
    if (finalR.worst > strandedLimit) {
      const why = plan.warnings.length > 0
        ? ` Likely cause: ${plan.warnings.join('; ')}.`
        : ' The operating window, the path budget or the set roster cannot cover them.'
      log.push(`⚠ FINAL CHECK FAILED: ${Math.round(finalR.worst)} passengers a day, on the busiest day-type, are still without a seat home on the finished timetable with the assigned rakes, against an allowance of ${Math.round(maxUnreturned)} a day.${why}`)
    } else {
      log.push(maxUnreturned > 0
        ? `Final check: ${Math.round(finalR.worst)} a day without a seat home on the busiest day-type under the ASSIGNED rakes, within the allowance of ${Math.round(maxUnreturned)} a day.`
        : 'Final check: everyone comes home on every day-type under the ASSIGNED rakes — the no-unreturned rule holds.')
    }
  }

  // A day that runs no revenue working repositions nothing: the empty-stock
  // moves that were added to serve workings since pruned come out too, or a
  // month that "runs nothing" would still pay for a diesel out and back
  // (September Pink, 2026-09-04: 0 turns, minus £3,380 a day).
  if (deps.length > 0 && !deps.some((d) => d.enabled && !d.positioning)) {
    log.push('No revenue working survived, so the empty-stock moves that served them are withdrawn too — a day that runs nothing repositions nothing.')
    deps = []
    plan = planFor(deps)
  }

  // Fleet-cap check on the finished article: a plan that needs a fourth steam
  // engine is not a plan.
  const finalCost = costFromDeps(net, deps, rates, dieselLoco, fleet, plan.carriages, plan.setsInTraffic)
  for (const msg of finalCost.overCap) log.push(`⚠ Fleet: ${msg}`)
  // No silent pass: an engine that would appear from nowhere or be left behind
  // is named here and carried into the month's issues (Chris, 2026-09-07).
  for (const lo of finalCost.locoOut) log.push(`⚠ Locomotive: ${lo.text} — every engine starts and ends its day at Grosmont or Pickering.`)
  if (objective === 'profit' && deps.some((d) => d.enabled)) {
    const finalR = run(deps)
    log.push(`Economics: revenue ${gbp(finalR.revenue)}/${period} · marginal running costs ${gbp(finalCost.marginal * w)}/${period} · day-fixed infra & overhead ${gbp(finalCost.dayFixed * w)}/${period} (borne by the operating day, shared by every train that runs).`)
  }

  return { deps, log, plan }
}
